import { Link, type LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * A link to another page, or a button that switches a view in place ("See all 40 in table"). The
 * two never mix: a `to` navigates, an `onClick` does not.
 */
export type SeeAllTarget =
  | {
      to: NonNullable<LinkProps['to']>
      search?: LinkProps['search']
      /** A route with a path param (`/mutual-funds/fund/$symbol`). TextLink reads it. */
      params?: LinkProps['params']
      onClick?: undefined
    }
  | {
      onClick: () => void
      to?: undefined
      search?: undefined
      /** A button that opens a dialog says so (mf:an, Sector diversity's "See all"). */
      'aria-haspopup'?: 'dialog' | undefined
    }

type SeeAllLinkProps = SeeAllTarget & {
  /** The words, when a section needs more than "See all": "See all rankings". */
  children?: ReactNode
  /** `rail` for the rail's section heads, one step smaller. Default `main`. */
  size?: 'main' | 'rail' | undefined
  /**
   * A fuller name for a page with several: "See all market updates". Start it with the words on
   * screen, so a member using voice control can say what they see (WCAG 2.5.3).
   */
  'aria-label'?: string | undefined
  className?: string | undefined
}

/**
 * The "See all ▸" at the end of a section head (Figma `link/section`, 442 instances).
 *
 * Grey, not green: it is a way on to more of the same, not the page's call to action, and every
 * drawn instance is #9a9a9a whatever one frame's note says. Hover and keyboard focus underline it,
 * take it to the foreground colour and nudge the ▸ 2px on.
 *
 * The ▸ is drawn, not typed: a text glyph takes the font's own triangle, which is neither the
 * frame's filled 6px mark nor the same size in IBM Plex and the fallbacks. Figma 6 → `size-2`
 * (4-grid). It is hidden from assistive tech — the words are the whole name. The mock sets the
 * mark 6px after the words with its triangle 2px into its box; the 6 snaps to 8 on the grid, so
 * the triangle sits flush at its box's start instead and the words-to-mark gap still reads 8.
 * Only the words underline (offset 3px), as the mock's `span` does.
 *
 * An invisible `::before` reaches 8px past each end and 44px tall below `lg`, as the text actions
 * in EmptyState do, without moving anything; from `lg` it keeps 24, the WCAG 2.5.8 floor, since
 * the words' own line is 19–21. Centred on the link rather than inset from it, so the reach is
 * the same whichever type size the link is set in.
 */
export default function SeeAllLink({
  children = 'See all',
  size = 'main',
  className,
  'aria-label': ariaLabel,
  ...target
}: SeeAllLinkProps) {
  const classes = cn(
    'group/see-all relative inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground',
    size === 'rail' ? 'text-body-s' : 'text-body-m',
    "touch-manipulation before:absolute before:-inset-x-2 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-[''] lg:before:h-6",
    className,
  )
  const content = (
    <>
      <span className="underline-offset-3 group-hover/see-all:underline group-focus-visible/see-all:underline">
        {children}
      </span>
      <svg
        viewBox="0 0 8 8"
        aria-hidden="true"
        className="size-2 shrink-0 fill-current transition-[translate] duration-med ease-out group-hover/see-all:motion-safe:translate-x-(--motion-nudge) group-focus-visible/see-all:motion-safe:translate-x-(--motion-nudge)"
      >
        <path d="M0 1v6l5-3z" />
      </svg>
    </>
  )

  if (target.onClick !== undefined) {
    return (
      <button
        type="button"
        onClick={target.onClick}
        aria-label={ariaLabel}
        aria-haspopup={target['aria-haspopup']}
        className={classes}
      >
        {content}
      </button>
    )
  }

  return (
    <Link
      to={target.to}
      {...(target.search === undefined ? {} : { search: target.search })}
      aria-label={ariaLabel}
      className={classes}
    >
      {content}
    </Link>
  )
}
